/**
 * Feldbezeichnungen der Falldaten für „Cockpit durchsuchen“ im Nachrichten-
 * fenster (Inhaber-Feedback 15.08.26, I.6). Generiert aus
 * backend/resources/fields_*.js (key → label je Datenbereich); bei Änderungen
 * an den Ressourcen neu erzeugen. Reine Daten – keine Logik.
 */

export interface SubjectFieldSection {
  /** Kurzer Präfix für die Chip-Beschriftung (z. B. „Kunde“). */
  prefix: string;
  /** Feldschlüssel → Bezeichnung. */
  labels: Record<string, string>;
}

export const SUBJECT_FIELD_LABELS: Record<string, SubjectFieldSection> = {
  // Manuell: Stammdaten der Akte (kein fields_*.js vorhanden).
  case: {
    prefix: "Akte",
    labels: {
      "caseNumber": "Aktenzeichen",
      "caseDescription": "Bezeichnung",
    },
  },
  clients: {
    prefix: "Kunde",
    labels: {
      "clientType": "Person oder Firma",
      "company": "Firma",
      "salutation": "Anrede",
      "title": "Titel",
      "firstName": "Vorname",
      "lastName": "Nachname",
      "birthdate": "Geburtsdatum",
      "languages": "Welche Sprachen sprechen Sie?",
      "streetNo": "Straße / Nr.",
      "zip": "PLZ",
      "city": "Ort",
      "country": "Land",
      "mobile": "Mobilfunknummer",
      "landline": "Festnetz",
      "email": "E-Mail Adresse",
      "homepage": "Ihre Homepage",
      "personDamage": "Wurden Sie verletzt?",
      "personDamageToBeClaimed": "Sollen Ansprüche wegen dieser Verletzung geltend gemacht werden?",
      "personDamageTreated": "Wurden und/oder werden Sie wegen dieser Verletzung behandelt/untersucht?",
      "personDamageInjuries": "Welche Verletzungen haben Sie durch den Unfall erlitten?",
      "roleOwner": "Ich bin Eigentümer des Fahrzeuges",
      "roleHolder": "Ich bin Halter des Fahrzeuges",
      "roleDriver": "Ich war Fahrer des Fahrzeuges zum Unfallzeitpunkt",
      "roleInsured": "Ich bin Versicherungsnehmer der Haftpflicht- und/oder Kaskoversicherung des Fahrzeuges",
      "roleLeasing": "Ich bin Leasing-/ Darlehensnehmer des Fahrzeuges",
      "legalExpenseInsuranceExists": "Haben Sie für diesen konkreten Unfall eine Rechtsschutzversicherung?",
      "legalExpenseInsuranceName": "Name der Versicherung",
      "legalExpenseInsuranceDeductible": "Selbstbeteiligung",
      "legalExpenseInsurancePolicyNumber": "Versicherungsscheinnummer",
      "legalExpenseInsuranceCaseNumber": "Schadennummer",
      "legalExpenseInsuranceCoverageAsked": "Wurde die Deckung angefragt?",
      "legalExpenseInsuranceCoverage": "Sind die außergerichtlichen Kosten gedeckt?",
      "iban": "IBAN",
      "accountHolder": "Kontoinhaber",
      "seizureProtectionAccount": "Handelt es sich dabei um ein Pfändungsschutzkonto?",
      "remarks": "Haben Sie weitere Anmerkungen?",
    },
  },
  vehicles: {
    prefix: "Fahrzeug",
    labels: {
      "category": "Was wurde beschädigt?",
      "subcategory": "Unterkategorie",
      "subcategoryOther": "Beschreibung bei \"Sonstiges\"",
      "manufacturer": "Hersteller",
      "model": "Modell",
      "yearOfConstruction": "Baujahr",
      "firstRegistration": "Datum der Erstzulassung",
      "serviceInAuthorizedWorkshop": "Bisher immer in Markenwerkstatt?",
      "serviceProof": "Nachweis",
      "financingType": "Leasing/Finanzierung",
      "hasTrailer": "War das Fahrzeug Teil eines Gespanns?",
      "registration": "Hatte das Fahrzeug zum Unfallzeitpunkt eine Zulassung (Kennzeichen)?",
      "licensePlate": "Amtliches Kennzeichen",
      "licenseCountry": "Zulassungsland",
      "insuranceAvailable": "Bestand für das Fahrzeug zum Unfallzeitpunkt eine Versicherung?",
      "insuranceType": "Wie ist Ihr Fahrzeug versichert?",
      "insuranceName": "Name der Versicherung",
      "insuranceDeductible": "Selbstbeteiligung Privat-/Betriebshaftpflicht",
      "insuranceDeductibleFull": "Selbstbeteiligung in Vollkasko",
      "insuranceDeductiblePartial": "Selbstbeteiligung in Teilkasko",
      "insurancePolicyNumber": "Versicherungsscheinnummer",
      "insuranceCaseNumber": "Falls es für diesen Schaden schon eine Schadennummer gibt, bitte hier eintragen",
      "previousDamage": "Gibt es am Fahrzeug einen Vorschaden?",
      "previousDamageSameArea": "Im selben Bereich wie der jetzige neue Unfallschaden?",
      "previousDamageDescription": "Genaue Beschreibung des Vorschadens",
      "previousDamageInvoice": "Gibt es hierzu eine Rechnung?",
      "previousDamageInsuranceClaim": "Wurde dieser Schaden über eine Versicherung abgerechnet?",
      "previousDamageInvoiceSubmitted": "Wurde eine entsprechende Rechnung bei der Versicherung eingereicht?",
      "existingDamage": "Gibt es am Fahrzeug einen bestehenden Altschaden?",
      "existingDamageSameArea": "Im selben Bereich wie der jetzige neue Unfallschaden?",
      "existingDamageDescription": "Genaue Beschreibung des Altschadens",
      "existingDamageInvoice": "Gibt es hierzu eine Rechnung?",
      "existingDamageInsuranceClaim": "Wurde dieser Schaden über eine Versicherung abgerechnet?",
      "existingDamageInvoiceSubmitted": "Wurde eine entsprechende Rechnung bei der Versicherung eingereicht?",
      "foreignProtectionInsuranceAvailable": "Besteht für das Fahrzeug eine Auslandsschadenschutzversicherung?",
      "foreignProtectionInsuranceName": "Name der Versicherung",
      "foreignProtectionInsuranceDeductible": "Selbstbeteiligung",
      "foreignProtectionInsurancePolicyNumber": "Versicherungsscheinnummer",
      "foreignProtectionInsuranceCaseNumber": "Schadennummer",
      "protectionBriefInsuranceAvailable": "Schutzbrief vorhanden?",
      "protectionBriefInsuranceName": "Name der Versicherung",
      "protectionBriefInsuranceDeductible": "Selbstbeteiligung",
      "protectionBriefInsurancePolicyNumber": "Versicherungsscheinnummer",
      "protectionBriefInsuranceCaseNumber": "Schadennummer",
      "gapInsuranceAvailable": "GAP-Versicherung vorhanden?",
      "gapInsuranceName": "Name der Versicherung",
      "gapInsuranceDeductible": "Selbstbeteiligung",
      "gapInsurancePolicyNumber": "Versicherungsscheinnummer",
      "gapInsuranceCaseNumber": "Schadennummer",
      "financingCompany": "Gesellschaft",
      "financingContractNumber": "Vertragsnummer",
      "remarksVehicle": "Haben Sie weitere Anmerkungen?",
    },
  },
  opponents: {
    prefix: "Gegner",
    labels: {
      "category": "Fahrzeug/Sonstiges",
      "subcategory": "Unterkategorie",
      "subcategoryOther": "Beschreibung bei \"Sonstiges\"",
      "otherAgeCategory": "Alterskategorie",
      "registration": "Hatte das Fahrzeug des Unfallgegners zum Unfallzeitpunkt eine Zulassung (Kennzeichen)",
      "registrationKnown": "Ist Ihnen das Kennzeichen und Zulassungsland bekannt?",
      "licensePlate": "Amtliches Kennzeichen",
      "licenseCountry": "Zulassungsland",
      "holderDataAvailable": "Wissen Sie, wer Halter des gegnerischen Fahrzeuges ist (Eintrag im Fahrzeugschein)?",
      "holderType": "Person oder Firma",
      "holderCompany": "Firma",
      "holderSalutation": "Anrede",
      "holderTitle": "Titel",
      "holderFirstName": "Vorname",
      "holderLastName": "Nachname",
      "holderStreetNo": "Straße / Nr.",
      "holderZip": "PLZ",
      "holderCity": "Ort",
      "holderCountry": "Land",
      "holderPhone": "Telefonnummer",
      "holderEmail": "E-Mail Adresse",
      "holderHomepage": "Homepage",
      "holderRepresentedByLawyer": "Anwaltlich vertreten?",
      "holderLawyerCaseNo": "Aktenzeichen des Anwalts",
      "holderLawfirm": "Name der Kanzlei",
      "holderLawyer": "Name des Anwalts",
      "driverDataAvailable": "Haben Sie die Daten des Fahrers?",
      "driverSalutation": "Anrede",
      "driverTitle": "Titel",
      "driverFirstName": "Vorname",
      "driverLastName": "Nachname",
      "driverStreetNo": "Straße / Nr.",
      "driverZip": "PLZ",
      "driverCity": "Ort",
      "driverCountry": "Land",
      "driverPhone": "Telefonnummer",
      "driverEmail": "E-Mail Adresse",
      "driverHomepage": "Homepage",
      "driverRepresentedByLawyer": "Anwaltlich vertreten?",
      "driverLawyerCaseNo": "Aktenzeichen des Anwalts",
      "driverLawfirm": "Name der Kanzlei",
      "driverLawyer": "Name des Anwalts",
      "otherType": "Person oder Firma",
      "otherCompany": "Firma",
      "otherSalutation": "Anrede",
      "otherTitle": "Titel",
      "otherFirstName": "Vorname",
      "otherLastName": "Nachname",
      "otherStreetNo": "Straße / Nr.",
      "otherZip": "PLZ",
      "otherCity": "Ort",
      "otherCountry": "Land",
      "otherPhone": "Telefonnummer",
      "otherEmail": "E-Mail Adresse",
      "otherHomepage": "Homepage",
      "otherRepresentedByLawyer": "Anwaltlich vertreten?",
      "otherLawyerCaseNo": "Aktenzeichen des Anwalts",
      "otherLawfirm": "Name der Kanzlei",
      "otherLawyer": "Name des Anwalts",
      "mother": "Sind die Daten der Mutter bekannt?",
      "motherSalutation": "Anrede",
      "motherTitle": "Titel",
      "motherFirstName": "Vorname",
      "motherLastName": "Nachname",
      "motherStreetNo": "Straße / Nr.",
      "motherZip": "PLZ",
      "motherCity": "Ort",
      "motherCountry": "Land",
      "motherPhone": "Telefonnummer",
      "motherEmail": "E-Mail Adresse",
      "motherRepresentedByLawyer": "Anwaltlich vertreten?",
      "motherLawyerCaseNo": "Aktenzeichen des Anwalts",
      "motherLawfirm": "Name der Kanzlei",
      "motherLawyer": "Name des Anwalts",
      "father": "Sind die Daten des Vaters bekannt?",
      "fatherSalutation": "Anrede",
      "fatherTitle": "Titel",
      "fatherFirstName": "Vorname",
      "fatherLastName": "Nachname",
      "fatherStreetNo": "Straße / Nr.",
      "fatherZip": "PLZ",
      "fatherCity": "Ort",
      "fatherCountry": "Land",
      "fatherPhone": "Telefonnummer",
      "fatherEmail": "E-Mail Adresse",
      "fatherRepresentedByLawyer": "Anwaltlich vertreten?",
      "fatherLawyerCaseNo": "Aktenzeichen des Anwalts",
      "fatherLawfirm": "Name der Kanzlei",
      "fatherLawyer": "Name des Anwalts",
      "insuranceDataAvailable": "Haben Sie die Versicherungsdaten des Gegners schon?",
      "insuranceType": "Versicherungstyp",
      "insuranceName": "Name der Versicherung",
      "insurancePolicyNumber": "Versicherungsscheinnummer",
      "insuranceCaseNumber": "Gibt es für diesen Unfallschaden schon eine Schadennummer?",
      "insurancePhone": "Telefonnummer",
      "insuranceEmail": "E-Mail Adresse",
      "insuranceContact": "Name des Sachbearbeiters",
      "insuranceRepresentedByLawyer": "Anwaltlich vertreten?",
      "insuranceLawyerCaseNo": "Aktenzeichen des Anwalts",
      "insuranceLawfirm": "Name der Kanzlei",
      "insuranceLawyer": "Name des Anwalts",
      "adjusterDataAvailable": "Daten zum Regulierungsbeauftr. vorhanden?",
      "adjusterName": "Name der Versicherung",
      "adjusterPolicyNumber": "Versicherungsscheinnummer",
      "adjusterCaseNumber": "Gibt es für diesen Unfallschaden schon eine Schadennummer?",
      "adjusterPhone": "Telefonnummer",
      "adjusterEmail": "E-Mail Adresse",
      "adjusterContact": "Name des Sachbearbeiters",
      "adjusterRepresentedByLawyer": "Anwaltlich vertreten?",
      "adjusterLawyerCaseNo": "Aktenzeichen des Anwalts",
      "adjusterLawfirm": "Name der Kanzlei",
      "adjusterLawyer": "Name des Anwalts",
      "remarks": "Haben Sie weitere Anmerkungen?",
    },
  },
  accident: {
    prefix: "Unfall",
    labels: {
      "accidentDate": "Unfalldatum",
      "accidentTime": "Unfalluhrzeit (ca.)",
      "streetType": "Auf welcher Art Straße sind Sie gefahren?",
      "streetNo": "Straßennummer/Bezeichnung/Kreuzung",
      "zip": "Postleitzahl",
      "city": "Ort",
      "country": "Land",
      "locationType": "Innerorts/Außerorts",
      "driverClientVehicle": "Wer ist Ihr Fahrzeug gefahren?",
      "driverId": "Name des Fahrers",
      "accidentDescription": "Unfallhergang",
      "damageClientVehicle": "Kontaktstelle an Ihrem Fahrzeug",
      "damageOpponentVehicle": "Kontaktstelle am Fahrzeug/Objekt des/der Gegner(s)",
      "clientAdmittedFault": "Haben Sie die Schuld eingeräumt?",
      "clientAdmittedFaultTo": "Wem gegenüber?",
      "clientAdmittedFaultHow": "Wie haben Sie sich geäußert?",
      "clientAdmittedFaultStatement": "Was haben Sie gesagt?",
      "opponentAdmittedFault": "Hat der Gegner die Schuld eingeräumt?",
      "opponentAdmittedFaultTo": "Wem gegenüber?",
      "opponentAdmittedFaultHow": "Wie hat der Gegner sich geäußert?",
      "opponentAdmittedFaultStatement": "Was hat der Gegner gesagt?",
      "evidenceAvailable": "Sind Beweismittel vorhanden?",
      "evidenceTypes": "Welche Beweismittel?",
      "rentalCarRequired": "Wird aufgrund fehlender Fahrtauglichkeit oder Verkehrssicherheit des Fahrzeugs ein Mietwagen benötigt?",
      "towingRequired": "Musste das Fahrzeug durch einen Abschleppdienst geborgen werden?",
      "remarks": "Haben Sie weitere Anmerkungen?",
    },
  },
  witnesses: {
    prefix: "Zeuge",
    labels: {
      "witnessType": "Wem hilft die Aussage des Zeugen?",
      "roles": "Wie war der Zeuge beteiligt?",
      "relation": "Bezug zum Mandanten/Gegner",
      "injured": "Wurde der Zeuge verletzt?",
      "salutation": "Anrede",
      "title": "Titel",
      "firstName": "Vorname",
      "lastName": "Nachname",
      "contactDataAvailable": "Haben Sie die Kontaktdaten des Zeugen?",
      "streetNo": "Straße / Nr.",
      "zip": "PLZ",
      "city": "Ort",
      "country": "Land",
      "mobile": "Mobilfunknummer",
      "landline": "Festnetz",
      "email": "E-Mail Adresse",
      "remarks": "Haben Sie weitere Anmerkungen?",
    },
  },
  policeProsecution: {
    prefix: "Polizei/StA",
    labels: {
      "policeInvolved": "War die Polizei vor Ort oder sonst beteiligt?",
      "prosecutionInvolved": "Ist die Angelegenheit bereits an eine Staatsanwaltschaft abgegeben worden?",
      "policeStation": "Dienststelle",
      "policeCaseNumber": "Aktenzeichen",
      "policeContactPerson": "Haben Sie die Daten des Ansprechpartners?",
      "policeSalutation": "Anrede",
      "policeTitle": "Titel",
      "policeFirstName": "Vorname",
      "policeLastName": "Nachname",
      "policePhone": "Telefonnummer",
      "policeEmail": "E-Mail Adresse",
      "policeWarningClient": "Wurden Sie polizeilich verwarnt?",
      "policeWarningOpponent": "Wurde der Unfallgegner polizeilich verwarnt?",
      "policeWarningClientDescription": "Beschreibung der Verwarnung (Mandant)",
      "policeWarningOpponentDescription": "Beschreibung der Verwarnung (Gegner)",
      "prosecutionStation": "Name der Staatsanwaltschaft",
      "prosecutionCaseNumber": "Aktenzeichen",
      "prosecutionContactPerson": "Haben Sie die Daten des Ansprechpartners?",
      "prosecutionSalutation": "Anrede",
      "prosecutionTitle": "Titel",
      "prosecutionFirstName": "Vorname",
      "prosecutionLastName": "Nachname",
      "prosecutionPhone": "Telefonnummer",
      "prosecutionEmail": "E-Mail Adresse",
      "remarks": "Haben Sie weitere Anmerkungen?",
    },
  },
  settlement: {
    prefix: "Werte",
    labels: {
      "replacementValue": "Wiederbeschaffungswert (WBW)",
      "residualValue": "Restwert (RW)",
      "repairCostsEstimate": "Reparaturkosten lt. Gutachten/KVA (brutto)",
      "repairCostsInvoice": "Reparaturkosten lt. Rechnung (brutto)",
      "impairment": "Merkantile Wertminderung (WM)",
      "reparaturkostenAngegeben": "Reparaturkosten im Gutachten angegeben?",
      "wbwRestwertAngegeben": "WBW und Restwert im Gutachten angegeben?",
      "besteuerung": "Besteuerung des WBW",
      "differenzbesteuerung": "Differenzsteuer-Satz",
      "wertminderungErmitteltNach": "Wertminderung ermittelt nach",
      "wertverbesserungBrutto": "Wertverbesserung (brutto)",
      "kostenNotreparaturBrutto": "Kosten Notreparatur (brutto)",
      "reparaturdauerArbeitstage": "Reparaturdauer in Arbeitstagen",
      "wiederbeschaffungsdauerTage": "Wiederbeschaffungsdauer in Tagen",
      "basisMietwagenklasse": "Basis für Mietwagenklasse",
      "settlement": "Wie wollen Sie voraussichtlich abrechnen?",
      "calculationbasis": "Auf welcher Basis erfolgt die Abrechnung?",
      "vat": "Sind Sie für das Fahrzeug vorsteuerabzugsberechtigt?",
      "vatShare": "Anteil (%)",
      "repair": "Soll (falls möglich) eine Reparatur des Fahrzeuges durchgeführt werden?",
      "repairType": "Wird die Reparatur Privat oder in einer Werkstatt mit Rechnung vorgenommen?",
      "repairWorkshop": "Bei welchem Reparaturbetrieb?",
      "repairStarted": "Hat die Reparatur bereits begonnen?",
      "repairDone": "Ist die Reparatur bereits abgeschlossen?",
      "repairFrom": "Reparatur von",
      "repairTo": "Reparatur bis",
      "repairScheduled": "Wurde ein Reparaturtermin vereinbart?",
      "repairScheduledDate": "Wann soll die Reparatur beginnen?",
      "repairPlannedDate": "Wann ist die Reparatur geplant?",
      "repairNote": "Hinweis",
      "continueUsingAccidentVehicle": "Wird das Fahrzeug (falls möglich) unrepariert weitergenutzt?",
      "moreThanSixMonthsUse": "Weiternutzung länger als 6 Monate?",
      "sellAccidentVehicle": "Wird das Fahrzeug verkauft?",
      "soldAlready": "Wurde das Fahrzeug bereits verkauft?",
      "soldFor": "Zu welchem Preis?",
      "soldForEquals": "Entspricht",
      "soldContract": "Ist ein Kaufvertrag vorhanden?",
      "replaced": "Wird ein Ersatzfahrzeug beschafft?",
      "replacementDone": "Wurde bereits ein anderes Fahrzeug gekauft?",
      "replacedFor": "Zu welchem Bruttokaufpreis?",
      "replacedVat": "Anteil MwSt im Kaufpreis lt. Rechnung",
      "costEstimate": "Gibt es einen Kostenvoranschlag?",
      "costEstimateAccordingToClient": "Name der Werkstatt",
      "expertHired": "Wurde ein Gutachter beauftragt?",
      "expertAccordingToClient": "Name des Sachverständigenbüros (Firma)",
      "expertReport": "Haben Sie das Gutachten bereits?",
      "expertReportNumberAccordingToClient": "Gutachtennummer",
      "vehicleSafeAfterAccident": "War das Fahrzeug unmittelbar nach dem Unfall verkehrssicher?",
      "repairEmergencyPossible": "Ist lt. Gutachten/Werkstatt eine Notreparatur möglich?",
      "madeSafe": "Wurde das Fahrzeug verkehrssicher gemacht?",
      "madeSafePlanned": "Soll es noch verkehrssicher gemacht werden?",
      "emergencyRepairFrom": "Notreparatur von",
      "emergencyRepairUntil": "Notreparatur bis",
      "currentUse": "Wird das Fahrzeug derzeit genutzt (also bis zur Reparatur oder Verkauf)?",
      "rentalCar": "Benötigen oder hatten Sie einen Mietwagen?",
      "rentalCarAlready": "Haben Sie bereits einen Mietwagen genutzt oder derzeit in Benutzung?",
      "rentalCarDone": "Ist die Anmietung bereits abgeschlossen?",
      "rentalCarIntermediary": "Von wem wurde Ihnen der Mietwagen vermittelt?",
      "rentalCarCompany": "Name der Autovermietung",
      "rentalCarAlreadyFrom": "Von",
      "rentalCarAlreadyTo": "Bis",
      "rentalCarFuture": "Benötigen Sie zukünftig einen Mietwagen?",
      "rentalCarFutureFrom": "Voraussichtlich von",
      "rentalCarFutureTo": "Voraussichtlich bis",
      "lossOfUse": "Soll Nutzungsausfall geltend gemacht werden?",
      "holdingCosts": "Sollen Vorhaltekosten geltend gemacht werden?",
      "alternateVehiclesAvailable": "Stehen Ihnen andere Fahrzeuge zur Verfügung?",
      "alternateVehicleDetails": "Welche?",
      "alternateVehiclesThirdPartyLoss": "Muss dadurch eine andere Person auf die Nutzung dieses Fahrzeuges (teilweise) verzichten?",
      "vehiclesHeld": "Werden Fahrzeuge vorgehalten?",
      "heldVehicleDetails": "Welche?",
      "contactInsurance": "Hat bereits ein Kontakt mit der gegnerischen Versicherung stattgefunden?",
      "contactInsuranceHow": "Wie hat der Kontakt stattgefunden?",
      "contactInsuranceResponsible": "Hat die gegnerische Versicherung die Haftung bestätigt oder abgelehnt?",
      "contactInsuranceResponsibleAmount": "Zu welchem Anteil wurde die Haftung bestätigt (%)?",
      "contactInsuranceSettlement": "Gibt es ein Abrechnungsschreiben?",
      "contactInsuranceInspectionReport": "Hat die gegnerische Versicherung einen Prüfbereicht gesendet?",
      "contactInsuranceRentalCarOffer": "Hat die gegnerische Versicherung ein Mietwagenangebot unterbreitet?",
      "contactInsuranceReferenceRepairShop": "Hat die gegnerische Versicherung auf eine günstigere Werkstatt verwiesen?",
      "contactInsuranceOffer": "Hat die gegnerische Versicherung ein Restwertangebot unterbreitet?",
      "remarksSettlement": "Haben Sie weitere Anmerkungen?",
    },
  },
};
